import React, { useState } from 'react';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Icons } from '../components/Icons';

function ExcelManagement() {
  const { user } = useAuth();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // 엑셀 내보내기
  const exportToExcel = async (dataType) => {
    setExporting(true);
    try {
      let data = [];
      let filename = '';

      if (dataType === 'inventory') {
        // 재고 데이터 내보내기
        const inventoryRef = collection(db, 'inventory');
        const snapshot = await getDocs(inventoryRef);

        data = snapshot.docs.map(doc => {
          const item = doc.data();
          return {
            '바코드ID': item.barcodeId,
            '제품명': item.productName,
            '카테고리(대)': item.categoryLarge || '',
            '카테고리(중)': item.categoryMedium || '',
            '디자이너': item.designer || '',
            '수종/색상': item.material || '',
            '사이즈': item.size || '',
            '컨디션': item.condition || '',
            '위치': item.location,
            '입고일': item.arrivalDate || '',
            '소유구분': item.ownershipType || '직판',
            '복원완료': item.restorationCompleted ? '예' : '아니오',
            '구매가': item.purchasePrice || 0,
            '판매가': item.salePrice || 0,
            '위탁자': item.consignorName || '',
            '위탁자연락처': item.consignorContact || '',
            '위탁수수료': item.consignorCommission || 0,
            '수량': item.quantity || 1,
            '상태': item.status,
            '특이사항': item.notes || '',
            '등록일': item.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || '',
            '등록자': item.createdBy || ''
          };
        });
        filename = `재고목록_${new Date().toISOString().split('T')[0]}.xlsx`;

      } else if (dataType === 'sales') {
        // 판매 데이터 내보내기
        const salesRef = collection(db, 'sales');
        const snapshot = await getDocs(salesRef);

        data = snapshot.docs.map(doc => {
          const sale = doc.data();
          return {
            '판매일': sale.saleDate?.toDate?.()?.toLocaleDateString('ko-KR') || '',
            '제품명': sale.productName,
            '카테고리': sale.category,
            '구매가': sale.purchasePrice || 0,
            '원판매가': sale.originalSalePrice || 0,
            '실제판매가': sale.actualSalePrice,
            '수익': sale.profit,
            '위탁자': sale.consignorName || '',
            '위탁수수료': sale.commissionAmount || 0,
            '구매자': sale.buyerName || '',
            '연락처': sale.buyerContact || '',
            '결제방법': sale.paymentMethod,
            '메모': sale.notes || '',
            '등록일': sale.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || ''
          };
        });
        filename = `판매내역_${new Date().toISOString().split('T')[0]}.xlsx`;

      } else if (dataType === 'reservations') {
        // 예약 데이터 내보내기
        const reservationsRef = collection(db, 'reservations');
        const snapshot = await getDocs(reservationsRef);

        data = snapshot.docs.map(doc => {
          const reservation = doc.data();
          return {
            '고객명': reservation.customerName,
            '연락처': reservation.customerContact,
            '이메일': reservation.customerEmail || '',
            '제품명': reservation.productName,
            '예약일': reservation.reservationDate?.toDate?.()?.toLocaleDateString('ko-KR') || '',
            '만료일': reservation.expiryDate?.toDate?.()?.toLocaleDateString('ko-KR') || '',
            '계약금': reservation.depositAmount || 0,
            '상태': reservation.status === 'active' ? '예약중' : reservation.status === 'completed' ? '완료' : '취소',
            '메모': reservation.notes || '',
            '등록일': reservation.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || ''
          };
        });
        filename = `예약목록_${new Date().toISOString().split('T')[0]}.xlsx`;

      } else if (dataType === 'locations') {
        // 위치 데이터 내보내기
        const locationsRef = collection(db, 'locations');
        const snapshot = await getDocs(locationsRef);

        data = snapshot.docs.map(doc => {
          const location = doc.data();
          return {
            '위치명': location.name,
            '주소': location.address || '',
            '설명': location.description || '',
            '수용가능': location.capacity || '',
            '담당자': location.manager || '',
            '연락처': location.contact || '',
            '활성상태': location.active ? '활성' : '비활성',
            '등록일': location.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || ''
          };
        });
        filename = `위치목록_${new Date().toISOString().split('T')[0]}.xlsx`;
      }

      if (data.length === 0) {
        alert('내보낼 데이터가 없습니다.');
        return;
      }

      // 엑셀 파일 생성
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

      // 파일 저장
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, filename);

      alert(`${data.length}건의 데이터가 내보내기되었습니다.`);

    } catch (error) {
      console.error('엑셀 내보내기 오류:', error);
      alert('엑셀 내보내기 중 오류가 발생했습니다.');
    } finally {
      setExporting(false);
    }
  };

  // 유연한 컬럼 값 추출 함수
  const getColumnValue = (row, possibleNames) => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return row[name];
      }
    }
    return null;
  };

  // 엑셀 가져오기 (개선된 버전)
  const importFromExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResults(null);
    setImportProgress({ current: 0, total: 0 });

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          setImportProgress({ current: 0, total: jsonData.length });

          // 배치 처리 (50개씩)
          const BATCH_SIZE = 50;

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];

            try {
              // 유연한 컬럼 매핑
              const productName = getColumnValue(row, ['제품명', '제품', '상품명', '이름']);
              const sku = getColumnValue(row, ['SKU', 'sku', 'Sku', '바코드ID']);
              const categoryLarge = getColumnValue(row, ['카테고리(대)', '카테고리', '대분류']);
              const categoryMedium = getColumnValue(row, ['카테고리(중)', '중분류']);
              const designer = getColumnValue(row, ['디자이너', 'Designer', '제조사']);
              const material = getColumnValue(row, ['수종/색상', '수종', '색상', '재질']);
              const size = getColumnValue(row, ['사이즈', '크기', 'Size']);
              const condition = getColumnValue(row, ['컨디션', '상태']);
              const location = getColumnValue(row, ['위치', '창고', '보관위치', '장소']);
              const arrivalDate = getColumnValue(row, ['입고일', '입고날짜', '입고']);
              const ownershipType = getColumnValue(row, ['소유구분', '소유', '판매구분']);
              const restorationCompleted = getColumnValue(row, ['복원완료', '복원']);
              const purchasePrice = getColumnValue(row, ['구매가', '매입가', '원가']);
              const salePrice = getColumnValue(row, ['판매가', '판매금액', '가격']);
              const consignorName = getColumnValue(row, ['위탁자', '위탁자명']);
              const consignorContact = getColumnValue(row, ['위탁자연락처', '위탁자전화']);
              const consignorCommission = getColumnValue(row, ['위탁수수료', '수수료율']);
              const quantity = getColumnValue(row, ['수량', 'Quantity', '재고수량']);
              const notes = getColumnValue(row, ['특이사항', '비고', '메모', '설명']);

              // 필수 필드 검증
              if (!productName) {
                errors.push(`행 ${i + 2}: 제품명이 누락되었습니다.`);
                errorCount++;
                continue;
              }

              if (!location) {
                errors.push(`행 ${i + 2}: 위치가 누락되었습니다. (필수)`);
                errorCount++;
                continue;
              }

              // 바코드ID: SKU 우선, 없으면 자동생성
              const barcodeId = sku || `ONE${Date.now()}${i}${Math.random().toString(36).substr(2, 5).toUpperCase()}`;

              // 복원완료 값 처리
              const isRestorationCompleted = restorationCompleted === '예' || restorationCompleted === 'Y' || restorationCompleted === 'yes' || restorationCompleted === true;

              // 소유구분 값 처리 (기본값: 직판)
              const ownershipTypeValue = ownershipType === '위탁' ? '위탁' : '직판';

              const inventoryData = {
                barcodeId,
                productName,
                categoryLarge: categoryLarge || '',
                categoryMedium: categoryMedium || '',
                designer: designer || '',
                material: material || '',
                size: size || '',
                condition: condition || '중',
                location,
                arrivalDate: arrivalDate || '',
                ownershipType: ownershipTypeValue,
                restorationCompleted: isRestorationCompleted,
                status: '확인 필요',
                purchasePrice: parseInt(purchasePrice) || 0,
                salePrice: parseInt(salePrice) || 0,
                consignorName: ownershipTypeValue === '위탁' ? (consignorName || '') : '',
                consignorContact: ownershipTypeValue === '위탁' ? (consignorContact || '') : '',
                consignorCommission: ownershipTypeValue === '위탁' ? (parseFloat(consignorCommission) || 0) : 0,
                quantity: parseInt(quantity) || 1,
                notes: notes || '',
                locationQuantities: {},
                photos: [],
                attributes: {},
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: user.email,
                importedAt: serverTimestamp()
              };

              await addDoc(collection(db, 'inventory'), inventoryData);

              // 이력 기록
              await addDoc(collection(db, 'inventory_history'), {
                productId: barcodeId,
                changeType: '등록',
                timestamp: serverTimestamp(),
                userId: user.email,
                note: '엑셀 Import (자동매핑)'
              });

              successCount++;
              setImportProgress({ current: i + 1, total: jsonData.length });

              // 배치마다 잠깐 대기 (Firebase 부하 완화)
              if ((i + 1) % BATCH_SIZE === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }

            } catch (error) {
              errors.push(`행 ${i + 2}: ${error.message}`);
              errorCount++;
              setImportProgress({ current: i + 1, total: jsonData.length });
            }
          }

          setImportResults({
            total: jsonData.length,
            success: successCount,
            error: errorCount,
            errors: errors.slice(0, 20) // 최대 20개 오류 표시
          });

          if (successCount > 0) {
            alert(`✅ ${successCount}건의 데이터가 성공적으로 가져와졌습니다!`);
          }

        } catch (error) {
          console.error('파일 파싱 오류:', error);
          alert('엑셀 파일 파싱 중 오류가 발생했습니다.');
        } finally {
          setImporting(false);
          setImportProgress({ current: 0, total: 0 });
        }
      };
      reader.readAsBinaryString(file);

    } catch (error) {
      console.error('엑셀 가져오기 오류:', error);
      alert('엑셀 가져오기 중 오류가 발생했습니다.');
      setImporting(false);
    }

    // 파일 입력 초기화
    e.target.value = '';
  };

  // 템플릿 다운로드
  const downloadTemplate = () => {
    try {
      const templateData = [
        {
          'SKU': 'SKU-XXXXXXXX (선택사항, 없으면 자동생성)',
          '제품명': '빈티지 의자 (필수)',
          '카테고리(대)': 'SEATING',
          '카테고리(중)': 'CHAIR',
          '디자이너': 'arne jacobsen',
          '수종/색상': 'ivory',
          '사이즈': 'W450 x D500 x H800',
          '컨디션': '상',
          '위치': '자곡동 매장 (필수! 반드시 입력)',
          '입고일': '2025-01-01',
          '소유구분': '직판 (또는 위탁)',
          '복원완료': '아니오 (또는 예)',
          '구매가': '100000',
          '판매가': '150000',
          '위탁자': '',
          '위탁자연락처': '',
          '위탁수수료': '',
          '수량': '1',
          '특이사항': '메모나 설명'
        },
        {
          'SKU': '',
          '제품명': '예시 테이블',
          '카테고리(대)': 'TABLE',
          '카테고리(중)': 'DINING TABLE',
          '디자이너': '',
          '수종/색상': 'oak',
          '사이즈': '',
          '컨디션': '중',
          '위치': '누룽지 창고',
          '입고일': '',
          '소유구분': '위탁',
          '복원완료': '예',
          '구매가': '0',
          '판매가': '200000',
          '위탁자': '홍길동',
          '위탁자연락처': '010-1234-5678',
          '위탁수수료': '30',
          '수량': '1',
          '특이사항': ''
        }
      ];

      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '재고템플릿');

      // 엑셀 파일 생성
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });

      // 파일명을 더 간단하게 변경
      const fileName = `재고등록템플릿_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      console.log('템플릿 다운로드 성공:', fileName);
      alert('✅ 템플릿이 다운로드되었습니다!');
    } catch (error) {
      console.error('템플릿 다운로드 오류:', error);
      alert('❌ 템플릿 다운로드 중 오류가 발생했습니다: ' + error.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">엑셀 데이터 관리</h2>
          <p className="text-gray-500 mt-1">데이터를 엑셀로 내보내거나 대량으로 가져옵니다.</p>
        </div>
      </div>

      {/* 엑셀 내보내기 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-8">
        <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
          <Icons.Upload className="w-6 h-6 mr-2 text-brand-500 rotate-180" />
          데이터 내보내기 (Export)
        </h3>
        <p className="text-gray-600 mb-6">
          현재 시스템의 데이터를 엑셀 파일로 다운로드합니다. 백업이나 보고서 작성에 활용하세요.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <button
            onClick={() => exportToExcel('inventory')}
            disabled={exporting}
            className="group relative overflow-hidden p-6 bg-white border border-gray-200 hover:border-brand-300 rounded-xl transition-all duration-300 hover:shadow-md flex flex-col items-center text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <Icons.Box className="w-8 h-8" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1">재고 목록</h4>
              <p className="text-xs text-gray-500">전체 재고 데이터</p>
            </div>
          </button>

          <button
            onClick={() => exportToExcel('sales')}
            disabled={exporting}
            className="group relative overflow-hidden p-6 bg-white border border-gray-200 hover:border-brand-300 rounded-xl transition-all duration-300 hover:shadow-md flex flex-col items-center text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <Icons.DollarSign className="w-8 h-8" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1">판매 내역</h4>
              <p className="text-xs text-gray-500">매출 및 수익 데이터</p>
            </div>
          </button>

          <button
            onClick={() => exportToExcel('reservations')}
            disabled={exporting}
            className="group relative overflow-hidden p-6 bg-white border border-gray-200 hover:border-brand-300 rounded-xl transition-all duration-300 hover:shadow-md flex flex-col items-center text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <Icons.Clock className="w-8 h-8" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1">예약 목록</h4>
              <p className="text-xs text-gray-500">예약 및 만료 데이터</p>
            </div>
          </button>

          <button
            onClick={() => exportToExcel('locations')}
            disabled={exporting}
            className="group relative overflow-hidden p-6 bg-white border border-gray-200 hover:border-brand-300 rounded-xl transition-all duration-300 hover:shadow-md flex flex-col items-center text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <Icons.MapPin className="w-8 h-8" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1">위치 목록</h4>
              <p className="text-xs text-gray-500">창고 및 매장 위치</p>
            </div>
          </button>
        </div>

        {exporting && (
          <div className="mt-6 text-center text-brand-600 flex items-center justify-center bg-brand-50 p-3 rounded-lg animate-pulse">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600 mr-3"></div>
            <span className="font-medium">데이터를 엑셀로 변환하고 있습니다...</span>
          </div>
        )}
      </div>

      {/* 엑셀 가져오기 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-8">
        <h3 className="text-xl font-bold text-brand-900 mb-6 flex items-center">
          <Icons.Download className="w-6 h-6 mr-2 text-brand-500" />
          재고 데이터 가져오기 (Import)
        </h3>
        <p className="text-gray-600 mb-6">
          엑셀 파일에서 재고 데이터를 일괄 등록합니다. 대량의 데이터를 한 번에 등록할 때 유용합니다.
        </p>

        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 w-full">
              <h4 className="font-bold text-gray-900 mb-2">1. 템플릿 다운로드</h4>
              <p className="text-sm text-gray-500 mb-4">데이터 형식이 맞는 엑셀 템플릿을 먼저 다운로드하세요.</p>
              <button
                onClick={downloadTemplate}
                className="w-full px-4 py-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition font-medium flex items-center justify-center shadow-sm"
              >
                <Icons.FileText className="w-5 h-5 mr-2 text-gray-500" />
                템플릿 다운로드
              </button>
            </div>

            <div className="hidden md:block text-gray-300">
              <Icons.ChevronRight className="w-8 h-8" />
            </div>

            <div className="flex-1 w-full">
              <h4 className="font-bold text-gray-900 mb-2">2. 엑셀 파일 업로드</h4>
              <p className="text-sm text-gray-500 mb-4">작성한 엑셀 파일을 선택하여 업로드하세요.</p>
              <label className="w-full px-4 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition cursor-pointer flex items-center justify-center shadow-md font-bold">
                <Icons.Upload className="w-5 h-5 mr-2" />
                엑셀 파일 선택
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={importFromExcel}
                  disabled={importing}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {importing && (
            <div className="mt-6 bg-white rounded-lg border border-brand-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mr-3"></div>
                  <span className="font-bold text-brand-900 text-lg">데이터를 가져오는 중입니다...</span>
                </div>

                {/* 진행률 바 */}
                {importProgress.total > 0 && (
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span className="font-medium">진행률</span>
                      <span className="font-bold text-brand-600">
                        {importProgress.current} / {importProgress.total} ({Math.round((importProgress.current / importProgress.total) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-brand-600 h-3 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      예상 완료 시간: 약 {Math.ceil((importProgress.total - importProgress.current) * 0.5)}초
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 가져오기 결과 */}
          {importResults && (
            <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h4 className="font-bold text-gray-900 flex items-center">
                  <Icons.Check className="w-5 h-5 mr-2 text-green-500" />
                  가져오기 결과
                </h4>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-3xl font-bold text-gray-800 mb-1">{importResults.total}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase">전체</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-3xl font-bold text-green-600 mb-1">{importResults.success}</div>
                    <div className="text-xs font-bold text-green-600 uppercase">성공</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg border border-red-100">
                    <div className="text-3xl font-bold text-red-600 mb-1">{importResults.error}</div>
                    <div className="text-xs font-bold text-red-600 uppercase">실패</div>
                  </div>
                </div>

                {importResults.errors.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <h5 className="font-bold text-red-800 mb-3 flex items-center">
                      <Icons.AlertCircle className="w-4 h-4 mr-2" />
                      오류 목록
                    </h5>
                    <div className="space-y-2 text-sm text-red-700 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                      {importResults.errors.map((error, index) => (
                        <div key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{error}</span>
                        </div>
                      ))}
                      {importResults.error > 10 && (
                        <div className="text-gray-500 italic mt-2 pl-4">
                          ... 그 외 {importResults.error - 10}건의 오류
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 사용 안내 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 일반 안내 */}
        <div className="bg-blue-50 border border-blue-100 p-6 rounded-xl">
          <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center">
            <Icons.Info className="w-5 h-5 mr-2 text-blue-600" />
            사용 안내
          </h3>
          <ul className="space-y-3 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">1</span>
              <span><strong>템플릿 다운로드:</strong> 제공된 템플릿을 다운로드하여 형식을 확인하고 데이터를 입력하세요.</span>
            </li>
            <li className="flex items-start">
              <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">2</span>
              <span><strong>필수 필드:</strong> <span className="text-red-600 font-bold">제품명</span>과 <span className="text-red-600 font-bold">위치</span>는 반드시 입력해야 합니다.</span>
            </li>
            <li className="flex items-start">
              <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">3</span>
              <span><strong>자동 매핑:</strong> SKU, 바코드, 카테고리, 디자이너 등이 자동으로 시스템 필드에 매핑됩니다.</span>
            </li>
            <li className="flex items-start">
              <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">4</span>
              <span><strong>바코드ID:</strong> SKU가 바코드ID로 사용되며, 없으면 자동 생성됩니다.</span>
            </li>
            <li className="flex items-start">
              <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">5</span>
              <span><strong>대량 업로드:</strong> 279개 기준 약 2-3분 소요. 진행률이 실시간으로 표시됩니다.</span>
            </li>
          </ul>
        </div>

        {/* 컬럼 매핑 안내 */}
        <div className="bg-green-50 border border-green-100 p-6 rounded-xl">
          <h3 className="text-lg font-bold text-green-900 mb-4 flex items-center">
            <Icons.ArrowRight className="w-5 h-5 mr-2 text-green-600" />
            자동 컬럼 매핑
          </h3>
          <div className="space-y-3 text-sm text-green-800">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="font-bold text-green-900 mb-1">엑셀 → 시스템</div>
              <div className="space-y-1 text-xs">
                <div>• SKU → <span className="font-bold">바코드ID</span></div>
                <div>• 제품명 → <span className="font-bold">제품명</span></div>
                <div>• 바코드 → <span className="font-bold">설명</span> (자동 추가)</div>
                <div>• 카테고리(대/중) → <span className="font-bold">설명</span> (자동 추가)</div>
                <div>• 디자이너 → <span className="font-bold">설명</span> (자동 추가)</div>
                <div>• 위치 → <span className="font-bold">위치</span></div>
                <div>• 구매가/판매가 → <span className="font-bold">가격</span></div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="font-bold text-green-900 mb-1">고정 값</div>
              <div className="space-y-1 text-xs">
                <div>• 카테고리 = <span className="font-bold">"직판"</span> (자동)</div>
                <div>• 상태 = <span className="font-bold">"확인 필요"</span> (자동)</div>
                <div>• 컨디션 = <span className="font-bold">"중"</span> (자동)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExcelManagement;
